import { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@hopskipdrive/react-components';
import { AxiosError } from 'axios';

import { useAuthMutations } from '../../auth/hooks/useAuthMutations';
import { useAuthQuery } from '../../auth/hooks/useAuthQuery';
import { ServerError, ServerErrorProps } from '../../auth/pages/ServerError';

import { normalizeApiErrors } from './normalizeApiErrors';
import { axiosClient } from './provider';

const getResponseErrors = async (error: AxiosError) => {
  let data = error.response?.data;

  if (error.config?.localErrorHandling && data instanceof Blob) {
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(data as Blob);
      });

      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }

  return normalizeApiErrors(data && typeof data === 'object' && 'errors' in data ? data.errors : undefined);
};

interface AxiosErrorBoundaryProps {
  children: ReactElement;
}

export const AxiosErrorBoundary: React.FC<AxiosErrorBoundaryProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthQuery();
  const { handleSessionUnauthorized } = useAuthMutations();
  const [serverError, setServerError] = useState<ServerErrorProps>();

  useEffect(() => {
    const responseInterceptor = axiosClient.interceptors.response.use(undefined, async (error) => {
      if (error.config?.skipGlobalErrorHandling) {
        throw error;
      }

      if (error.response && error.response?.status) {
        const statusCode = error.response.status;
        const errorMessages = await getResponseErrors(error);

        const invalidCsrfToken = errorMessages.includes("Can't verify CSRF token authenticity.");

        if (error.config?.localErrorHandling && statusCode !== 401 && !invalidCsrfToken) {
          throw error;
        }

        switch (true) {
          case user && statusCode === 401:
            handleSessionUnauthorized(errorMessages);
            break;
          case statusCode === 403 || statusCode === 404:
            navigate(`${location.pathname}`, { state: { notFound: true } });
            break;
          case statusCode >= 500:
            setServerError({
              statusCode: statusCode,
              errorMessage: errorMessages[0] || 'Server error',
            });
            break;
          case invalidCsrfToken:
            handleSessionUnauthorized(['Your session has expired, please log back in']);
            break;
        }
      }

      throw error;
    });

    // To avoid the infinity loop if the latter also receives the response code 401, we eject the interceptor in this case
    return () => {
      axiosClient.interceptors.response.eject(responseInterceptor);
    };
  }, [user]);

  return serverError ? (
    <ThemeProvider>
      <ServerError statusCode={serverError.statusCode} errorMessage={serverError.errorMessage} />
    </ThemeProvider>
  ) : (
    children
  );
};
